import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import { buildCustomerRoute, isSubdomainStoreMode } from '../../utils/storeUtils';
import {
  TextField,
  Box,
  Alert,
  Typography,
  Paper,
  CircularProgress,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AuthHeaderActions from '../../components/auth/AuthHeaderActions';
import StoreAuthLogo, {
  getStoreAuthBackgroundSx,
  useStoreAuthBrandingReady,
} from '../../components/auth/StoreAuthBranding';
import PageLoadingSpinner from '../../components/common/PageLoadingSpinner';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import AuthService from '../../services/authService';
import PasswordField from '../../components/auth/PasswordField';
import GoogleSignInSection from '../../components/auth/GoogleSignInSection';
import LinkSentConfirmation from '../../components/auth/LinkSentConfirmation';
import useCustomerGoogleAuth from '../../hooks/useCustomerGoogleAuth';
import LegalFooterLinks from '../../components/legal/LegalFooterLinks';
import SalovarePhoneInput from '../../components/common/SalovarePhoneInput';
import { getBackendLanguageId } from '../../constants/Languages';
import { DEFAULT_PHONE_COUNTRY_CODE, toPhoneDto } from '../../utils/phoneUtils';
import { BRAND } from '../../theme/brandTokens';
import { brandBackdropSx, brandCardSx } from '../../theme/brandSurfaces';

const LOGIN_STEP = {
  EMAIL: 'email',
  PASSWORD: 'password',
  SET_PASSWORD: 'setPassword',
  CLAIM_PHONE: 'claimPhone',
  CLAIM_OTP: 'claimOtp',
  CLAIM_EMAIL: 'claimEmail',
};

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email.trim());

const backgroundStyle = {
  minHeight: { xs: '100dvh', sm: '100vh' },
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  ...brandBackdropSx,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  py: { xs: 2, sm: 0 },
  px: 0,
  touchAction: 'pan-y',
};

const cardStyle = {
  padding: { xs: '2rem 1.5rem', sm: '2.5rem 2rem' },
  ...brandCardSx,
  minWidth: { xs: 300, sm: 340 },
  maxWidth: { xs: 350, sm: 380 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const logoStyle = {
  fontWeight: 800,
  fontSize: { xs: '1.5rem', sm: '2rem' },
  letterSpacing: 2,
  color: BRAND.primaryText,
  marginBottom: '1.5rem',
  textAlign: 'center',
  width: '100%',
  maxWidth: '100%',
  lineHeight: 1.2,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const { loginCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { storeId, storeName, storeOverview, loading: storeLoading } = useStore();
  const loginLogoUrl = storeOverview?.brandingImages?.loginLogoUrl;
  const loginBackgroundUrl = storeOverview?.brandingImages?.loginBackgroundUrl;
  const authBrandingReady = useStoreAuthBrandingReady({
    logoUrl: loginLogoUrl,
    backgroundUrl: loginBackgroundUrl,
  });
  const [step, setStep] = useState(LOGIN_STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordSuccess, setSetPasswordSuccess] = useState(false);
  const [claimPhoneCountryCode, setClaimPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY_CODE);
  const [claimPhoneNumber, setClaimPhoneNumber] = useState('');
  const [claimCode, setClaimCode] = useState('');
  const [claimEmail, setClaimEmail] = useState('');
  const [claimToken, setClaimToken] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [claimInfo, setClaimInfo] = useState('');
  const searchParams = new URLSearchParams(location.search);
  const from = searchParams.get('redirect') || location.state?.from?.pathname;

  const resolvePostLoginPath = useCallback(() => {
    if (from && from !== '/login' && (from.includes('/stores/') || isSubdomainStoreMode())) {
      return from;
    }

    if (storeId) {
      return buildCustomerRoute(storeId, '');
    }

    return '/not-found';
  }, [from, storeId]);

  const { completeGoogleSignIn, googleLoading } = useCustomerGoogleAuth({
    onSuccess: () => navigate(resolvePostLoginPath(), { replace: true }),
    onError: () => setError('login.googleFailed'),
  });

  const handleGoogleSignInCallback = useCallback(async (response) => {
    setError('');
    await completeGoogleSignIn(response?.credential);
  }, [completeGoogleSignIn]);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (error) {
      setError('');
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (error && !error.startsWith('forgotPassword.')) {
      setError('');
    }
  };

  const handleBackToEmail = () => {
    setStep(LOGIN_STEP.EMAIL);
    setPassword('');
    setError('');
    setResetSuccess(false);
    setSetPasswordSuccess(false);
    setClaimPhoneCountryCode(DEFAULT_PHONE_COUNTRY_CODE);
    setClaimPhoneNumber('');
    setClaimCode('');
    setClaimEmail('');
    setClaimToken('');
    setClaimSuccess(false);
    setClaimInfo('');
    setClaimLoading(false);
  };

  const handleStartPhoneClaim = () => {
    setError('');
    setClaimInfo('');
    setClaimSuccess(false);
    setClaimCode('');
    setClaimEmail('');
    setClaimToken('');
    setStep(LOGIN_STEP.CLAIM_PHONE);
  };

  const getClaimPhoneDto = () => toPhoneDto(claimPhoneCountryCode, claimPhoneNumber);

  const handleRequestClaimOtp = async (event) => {
    event.preventDefault();
    setError('');
    const phone = getClaimPhoneDto();
    if (!phone) {
      setError('login.claim.phoneRequired');
      return;
    }

    setClaimLoading(true);
    try {
      await AuthService.requestCustomerPhoneClaimOtp({
        phone,
        language: getBackendLanguageId(i18n.language),
        storeId,
      });
      setClaimInfo('login.claim.codeSent');
      setStep(LOGIN_STEP.CLAIM_OTP);
    } catch {
      setError('login.claim.error');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleVerifyClaimOtp = async (event) => {
    event.preventDefault();
    setError('');
    const phone = getClaimPhoneDto();
    const code = claimCode.trim();
    if (!code) {
      setError('login.claim.codeRequired');
      return;
    }

    setClaimLoading(true);
    try {
      const result = await AuthService.verifyCustomerPhoneClaimOtp({ phone, code });
      setClaimToken(result.claimToken);
      setClaimInfo('');
      setStep(LOGIN_STEP.CLAIM_EMAIL);
    } catch {
      setError('login.claim.error');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleCompleteClaim = async (event) => {
    event.preventDefault();
    setError('');
    const trimmedClaimEmail = claimEmail.trim();
    if (!trimmedClaimEmail) {
      setError('login.emailRequired');
      return;
    }
    if (!isValidEmail(trimmedClaimEmail)) {
      setError('login.emailInvalid');
      return;
    }

    setClaimLoading(true);
    try {
      await AuthService.completeCustomerPhoneClaim({
        claimToken,
        email: trimmedClaimEmail,
        storeId,
      });
      setClaimEmail(trimmedClaimEmail);
      setClaimSuccess(true);
    } catch {
      setError('login.claim.error');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleResendClaimSetPassword = async () => {
    await AuthService.requestPasswordSetCustomer(claimEmail.trim(), storeId);
  };

  const handleContinue = async (event) => {
    event.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('login.emailRequired');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError('login.emailInvalid');
      return;
    }

    setEmailCheckLoading(true);
    try {
      const result = await AuthService.checkCustomerLoginEmail(trimmedEmail);

      if (!result.exists) {
        const registerPath = storeId ? buildCustomerRoute(storeId, 'register') : '/not-found';
        navigate(registerPath, {
          state: {
            email: trimmedEmail,
            from: from ? { pathname: from } : undefined,
          },
        });
        return;
      }

      setEmail(trimmedEmail);
      setStep(result.hasPassword ? LOGIN_STEP.PASSWORD : LOGIN_STEP.SET_PASSWORD);
    } catch {
      setError('login.emailCheckFailed');
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!password) {
      setError('login.passwordRequired');
      return;
    }

    setLoading(true);
    const result = await loginCustomer({ email: email.trim(), password });
    setLoading(false);

    if (result.success) {
      setTimeout(() => {
        navigate(resolvePostLoginPath(), { replace: true });
      }, 50);
    } else {
      setError(result.isNetworkError ? 'login.serverError' : 'login.invalid');
    }
  };

  const handleForgotPassword = async () => {
    setResetLoading(true);
    setError('');

    try {
      await AuthService.requestPasswordResetCustomer(email.trim());
      setResetSuccess(true);
    } catch {
      setError('forgotPassword.error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSetPasswordRequest = async () => {
    setSetPasswordLoading(true);
    setError('');

    try {
      await AuthService.requestPasswordSetCustomer(email.trim(), storeId);
      setSetPasswordSuccess(true);
    } catch {
      setError('firstAccess.error');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  if (storeLoading || !authBrandingReady) {
    return <PageLoadingSpinner />;
  }

  const isEmailStep = step === LOGIN_STEP.EMAIL;
  const isPasswordStep = step === LOGIN_STEP.PASSWORD;
  const isSetPasswordStep = step === LOGIN_STEP.SET_PASSWORD;
  const isClaimPhoneStep = step === LOGIN_STEP.CLAIM_PHONE;
  const isClaimOtpStep = step === LOGIN_STEP.CLAIM_OTP;
  const isClaimEmailStep = step === LOGIN_STEP.CLAIM_EMAIL;
  const isClaimStep = isClaimPhoneStep || isClaimOtpStep || isClaimEmailStep;
  const isBusy = loading || googleLoading || emailCheckLoading || claimLoading;

  return (
    <Box
      sx={{ ...backgroundStyle, ...getStoreAuthBackgroundSx(loginBackgroundUrl) }}
      onTouchMove={(event) => {
        event.stopPropagation();
      }}
    >
      <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', top: 24, right: 24 }}>
        <AuthHeaderActions />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            width: '100%',
            maxWidth: { xs: 350, sm: 380 },
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 3,
            pt: 2,
            pb: 2,
            minHeight: 56,
            boxSizing: 'border-box',
          }}
        >
          <AuthHeaderActions />
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: { xs: 0, sm: 3 },
            pb: { xs: 'calc(1rem + env(safe-area-inset-bottom, 0px) + 60px)', sm: 2 },
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            sx={{
              width: '100%',
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              px: { xs: 1, sm: 0 },
              boxSizing: 'border-box',
            }}
          >
            <Paper elevation={8} sx={cardStyle}>
              {loginLogoUrl ? (
                <StoreAuthLogo imageUrl={loginLogoUrl} storeName={storeName} />
              ) : storeName ? (
                <Typography sx={logoStyle} variant="h4" component="div">
                  {storeName}
                </Typography>
              ) : null}

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {t(error)}
                </Alert>
              )}

              {claimInfo && isClaimStep && !claimSuccess && (
                <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                  {t(claimInfo)}
                </Alert>
              )}

              {isEmailStep && (
                <Box component="form" noValidate onSubmit={handleContinue} sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 } }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t('login.email')}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={handleEmailChange}
                    sx={{ mb: 2, borderRadius: 2 }}
                    InputProps={{
                      style: { borderRadius: 12 },
                    }}
                  />
                  <PrimaryActionButton
                    type="submit"
                    fullWidth
                    sx={{
                      mt: 2,
                      mb: 0,
                      py: 1.5,
                      fontSize: '1.1rem',
                    }}
                    disabled={isBusy}
                    endIcon={emailCheckLoading && <CircularProgress size={22} color="inherit" />}
                  >
                    {emailCheckLoading ? t('login.continuing') : t('login.continue')}
                  </PrimaryActionButton>

                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={handleStartPhoneClaim}
                      sx={{
                        color: BRAND.accentText,
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {t('login.phoneOnlyCta')}
                    </Link>
                  </Box>

                  <GoogleSignInSection onCredential={handleGoogleSignInCallback} loading={googleLoading} />

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <LegalFooterLinks />
                  </Box>
                </Box>
              )}

              {isPasswordStep && (
                resetSuccess ? (
                  <LinkSentConfirmation
                    message={t('forgotPassword.success')}
                    email={email}
                    onResend={handleForgotPassword}
                    onBackToLogin={handleBackToEmail}
                  />
                ) : (
                  <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 } }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label={t('login.email')}
                      name="email"
                      value={email}
                      disabled
                      sx={{ mb: 2, borderRadius: 2 }}
                      InputProps={{
                        style: { borderRadius: 12 },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={handleBackToEmail}
                        sx={{
                          color: BRAND.accentText,
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('login.changeEmail')}
                      </Link>
                    </Box>
                    <PasswordField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label={t('login.password')}
                      id="password"
                      autoComplete="current-password"
                      autoFocus
                      value={password}
                      onChange={handlePasswordChange}
                      sx={{ mb: 1, borderRadius: 2 }}
                      InputProps={{
                        style: { borderRadius: 12 },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={handleForgotPassword}
                        disabled={resetLoading}
                        sx={{
                          color: BRAND.accentText,
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          '&:disabled': {
                            color: BRAND.textSubtle,
                            cursor: 'not-allowed',
                          },
                        }}
                      >
                        {resetLoading ? t('forgotPassword.sending') : t('forgotPassword.forgotPassword')}
                      </Link>
                    </Box>
                    <PrimaryActionButton
                      type="submit"
                      fullWidth
                      sx={{
                        mt: 2,
                        mb: 0,
                        py: 1.5,
                        fontSize: '1.1rem',
                      }}
                      disabled={loading || googleLoading}
                      endIcon={loading && <CircularProgress size={22} color="inherit" />}
                    >
                      {loading ? t('login.signingIn') : t('login.signIn')}
                    </PrimaryActionButton>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <LegalFooterLinks />
                    </Box>
                  </Box>
                )
              )}

              {isSetPasswordStep && (
                setPasswordSuccess ? (
                  <LinkSentConfirmation
                    message={t('firstAccess.success')}
                    email={email}
                    onResend={handleSetPasswordRequest}
                    onBackToLogin={handleBackToEmail}
                  />
                ) : (
                  <Box sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 } }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label={t('login.email')}
                      name="email"
                      value={email}
                      disabled
                      sx={{ mb: 2, borderRadius: 2 }}
                      InputProps={{
                        style: { borderRadius: 12 },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={handleBackToEmail}
                        sx={{
                          color: BRAND.accentText,
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('login.changeEmail')}
                      </Link>
                    </Box>
                    <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                      {t('login.setPasswordPrompt')}
                    </Alert>
                    <PrimaryActionButton
                      type="button"
                      fullWidth
                      onClick={handleSetPasswordRequest}
                      sx={{
                        mt: 2,
                        mb: 0,
                        py: 1.5,
                        fontSize: '1.1rem',
                      }}
                      disabled={setPasswordLoading}
                      endIcon={setPasswordLoading && <CircularProgress size={22} color="inherit" />}
                    >
                      {setPasswordLoading ? t('firstAccess.sending') : t('login.sendSetPasswordLink')}
                    </PrimaryActionButton>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <LegalFooterLinks />
                    </Box>
                  </Box>
                )
              )}

              {isClaimPhoneStep && (
                <Box component="form" noValidate onSubmit={handleRequestClaimOtp} sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 } }}>
                  <Box sx={{ width: '100%', mb: 2, mt: 1 }}>
                    <SalovarePhoneInput
                      label={t('login.claim.phone')}
                      countryCode={claimPhoneCountryCode}
                      number={claimPhoneNumber}
                      onCountryCodeChange={setClaimPhoneCountryCode}
                      onNumberChange={setClaimPhoneNumber}
                      required
                      disabled={claimLoading}
                      autoCompleteSection="claim"
                    />
                  </Box>
                  <PrimaryActionButton
                    type="submit"
                    fullWidth
                    sx={{ mt: 2, mb: 0, py: 1.5, fontSize: '1.1rem' }}
                    disabled={claimLoading}
                    endIcon={claimLoading && <CircularProgress size={22} color="inherit" />}
                  >
                    {claimLoading ? t('login.claim.sendingCode') : t('login.claim.sendCode')}
                  </PrimaryActionButton>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={handleBackToEmail}
                      sx={{
                        color: BRAND.accentText,
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {t('login.claim.back')}
                    </Link>
                  </Box>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <LegalFooterLinks />
                  </Box>
                </Box>
              )}

              {isClaimOtpStep && (
                <Box component="form" noValidate onSubmit={handleVerifyClaimOtp} sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 } }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="claim-code"
                    label={t('login.claim.code')}
                    name="claim-code"
                    autoComplete="one-time-code"
                    autoFocus
                    inputMode="numeric"
                    value={claimCode}
                    onChange={(event) => {
                      setClaimCode(event.target.value);
                      if (error) setError('');
                    }}
                    sx={{ mb: 2, borderRadius: 2 }}
                    InputProps={{
                      style: { borderRadius: 12 },
                    }}
                  />
                  <PrimaryActionButton
                    type="submit"
                    fullWidth
                    sx={{ mt: 2, mb: 0, py: 1.5, fontSize: '1.1rem' }}
                    disabled={claimLoading}
                    endIcon={claimLoading && <CircularProgress size={22} color="inherit" />}
                  >
                    {claimLoading ? t('login.claim.verifying') : t('login.claim.verify')}
                  </PrimaryActionButton>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        setError('');
                        setClaimCode('');
                        setStep(LOGIN_STEP.CLAIM_PHONE);
                      }}
                      sx={{
                        color: BRAND.accentText,
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {t('login.claim.back')}
                    </Link>
                  </Box>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <LegalFooterLinks />
                  </Box>
                </Box>
              )}

              {isClaimEmailStep && (
                claimSuccess ? (
                  <LinkSentConfirmation
                    message={t('login.claim.success')}
                    email={claimEmail}
                    onResend={handleResendClaimSetPassword}
                    onBackToLogin={handleBackToEmail}
                  />
                ) : (
                  <Box component="form" noValidate onSubmit={handleCompleteClaim} sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 } }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="claim-email"
                      label={t('login.email')}
                      name="claim-email"
                      autoComplete="email"
                      autoFocus
                      value={claimEmail}
                      onChange={(event) => {
                        setClaimEmail(event.target.value);
                        if (error) setError('');
                      }}
                      sx={{ mb: 2, borderRadius: 2 }}
                      InputProps={{
                        style: { borderRadius: 12 },
                      }}
                    />
                    <PrimaryActionButton
                      type="submit"
                      fullWidth
                      sx={{ mt: 2, mb: 0, py: 1.5, fontSize: '1.1rem' }}
                      disabled={claimLoading}
                      endIcon={claimLoading && <CircularProgress size={22} color="inherit" />}
                    >
                      {claimLoading ? t('login.claim.completing') : t('login.claim.complete')}
                    </PrimaryActionButton>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={handleBackToEmail}
                        sx={{
                          color: BRAND.accentText,
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {t('login.claim.back')}
                      </Link>
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <LegalFooterLinks />
                    </Box>
                  </Box>
                )
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
